/**
 * useSkinConfig.ts - Hook to access skin and template configurations
 * 
 * Provides easy access to Skinnr skin settings and Templatr template configs
 */

import { useSkinnr } from '@opcentral/dependr';

/**
 * Hook to access skin configuration
 * 
 * @example
 * const { getSkinConfig, getPluginConfig, getTemplateConfig } = useSkinConfig();
 * 
 * // Get outliner plugin config from active skin
 * const outlinerConfig = getPluginConfig('outliner');
 * const defaultView = outlinerConfig?.appearance?.defaultView || 'single';
 * 
 * // Get template config if template is active
 * const templateConfig = getTemplateConfig();
 * const saveMode = templateConfig?.dataSubmission?.saveMode || 'batch';
 */
export function useSkinConfig() {
  const skinnr = useSkinnr() as any;
  
  /**
   * Get the active skin configuration
   */
  const getSkinConfig = () => {
    const activeSkin = skinnr.getActiveSkin?.();
    return activeSkin || null;
  };
  
  /**
   * Get plugin configuration from active skin
   * @param pluginId - Plugin ID (e.g., 'outliner', 'pdfViewer')
   */
  const getPluginConfig = (pluginId: string) => {
    const skin = getSkinConfig();
    return skin?.plugins?.[pluginId] || null;
  };
  
  /**
   * Get data submission configuration from active skin
   */
  const getDataSubmissionConfig = () => {
    const skin = getSkinConfig();
    return skin?.dataSubmission || null;
  };
  
  /**
   * Get navigation configuration from active skin
   */
  const getNavigationConfig = () => {
    const skin = getSkinConfig();
    return skin?.navigation || null;
  };
  
  /**
   * Get development configuration from active skin
   */
  const getDevelopmentConfig = () => {
    const skin = getSkinConfig();
    return skin?.development || null;
  };
  
  /**
   * Get active template configuration (if template was applied via URL)
   */
  const getTemplateConfig = () => {
    return skinnr.activeTemplate?.config || null;
  };
  
  /**
   * Get merged configuration (template overrides skin)
   * @param path - Dot-notation path to config (e.g., 'plugins.outliner.appearance.defaultView')
   */
  const getConfig = (path: string, defaultValue?: any) => {
    const skin = getSkinConfig();
    const template = getTemplateConfig();
    
    // Helper to get nested value
    const getValue = (obj: any, path: string) => {
      return path.split('.').reduce((current, key) => current?.[key], obj);
    };
    
    // Template config takes precedence
    const templateValue = template ? getValue(template, path) : undefined;
    if (templateValue !== undefined) return templateValue;
    
    // Fall back to skin config
    const skinValue = skin ? getValue(skin, path) : undefined;
    if (skinValue !== undefined) return skinValue;
    
    // Fall back to default
    return defaultValue;
  };
  
  /**
   * Check if a template is active
   */
  const hasActiveTemplate = () => {
    return !!skinnr.activeTemplate;
  };
  
  /**
   * Get active template info
   */
  const getActiveTemplate = () => {
    return skinnr.activeTemplate || null;
  };
  
  return {
    // Skin accessors
    getSkinConfig,
    getPluginConfig,
    getDataSubmissionConfig,
    getNavigationConfig,
    getDevelopmentConfig,
    
    // Template accessors
    getTemplateConfig,
    hasActiveTemplate,
    getActiveTemplate,
    
    // Merged config accessor
    getConfig,
    
    // Direct skinnr access
    skinnr,
  };
}

export default useSkinConfig;
